import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Percent, Ticket, XCircle } from 'lucide-react';
import { couponResult } from '../utils/couponPresentation';
import { formatRupees, offerBenefitText } from '../utils/offerPresentation';
import { normalizeCouponCode } from '../hooks/useCartQuote';
import { focusRing } from '../styles/ui';

// Coupon entry for the cart. It collects intent only: typing changes a local
// draft and quotes nothing; Apply hands the code to the page, which makes it
// part of the cart quote's identity. Whether the code applies, and what it
// takes off, is read from that quote (see utils/couponPresentation).
//
//   submittedCode  the normalized code currently part of the quote, or null
//   quote          the CURRENT quote (null while loading / failed / idle)
//   quoteStatus    idle | loading | ready | error
//   canApply       the cart can be quoted (signed in, served location, items)
const CartCoupon = ({ submittedCode, quote, quoteStatus, canApply, isGuest, onApply, onRemove }) => {
  const [draft, setDraft] = useState('');
  const draftCode = normalizeCouponCode(draft);

  if (isGuest) {
    return (
      <div className="mb-5 rounded-xl border border-emerald-100 bg-emerald-50/50 px-4 py-3 text-sm text-ink-secondary">
        <Link to="/login?redirect=/cart" className={`font-semibold text-emerald-800 underline ${focusRing}`}>
          Sign in
        </Link>{' '}
        to use a coupon code.
      </div>
    );
  }

  const submit = (code) => {
    if (!code || !canApply) return;
    onApply(code);
    setDraft('');
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submit(draftCode);
  };

  const result = quote ? couponResult(quote, submittedCode) : { state: 'unchecked' };
  const suggestions = Array.isArray(quote?.availableCoupons) ? quote.availableCoupons.filter((offer) => offer?.code) : [];

  let status = null;
  if (submittedCode) {
    if (!canApply) {
      status = { tone: 'neutral', text: `We'll check ${submittedCode} once your cart can be priced here.` };
    } else if (quoteStatus === 'loading') {
      status = { tone: 'pending', text: `Checking ${submittedCode}…` };
    } else if (quoteStatus === 'error') {
      status = { tone: 'neutral', text: `We couldn't check ${submittedCode}. Try again above.` };
    } else if (result.state === 'applied') {
      const detail = result.freeDelivery
        ? ' Free delivery.'
        : result.savings !== null
        ? ` You save ${formatRupees(result.savings)}.`
        : '';
      status = { tone: 'success', text: `${submittedCode} applied.${detail}` };
    } else if (result.state === 'refused') {
      status = { tone: 'error', text: `${submittedCode} not applied. ${result.message}` };
    } else {
      status = { tone: 'neutral', text: `We couldn't check ${submittedCode} for this cart yet.` };
    }
  }

  const toneClass = {
    success: 'text-emerald-800 bg-emerald-50 border-emerald-100',
    error: 'text-amber-900 bg-amber-50 border-amber-100',
    pending: 'text-ink-secondary bg-white border-emerald-100',
    neutral: 'text-ink-secondary bg-white border-emerald-100',
  };
  const ToneIcon = { success: CheckCircle2, error: XCircle, pending: Loader2, neutral: Ticket };

  return (
    <div className="mb-4" data-testid="cart-coupon">
      <form onSubmit={handleSubmit} className="flex gap-2" noValidate>
        <label htmlFor="cart-coupon-code" className="sr-only">
          Coupon code
        </label>
        <span className="relative min-w-0 flex-1">
          <span
            className="absolute left-1.5 top-1/2 -translate-y-1/2 w-[34px] h-[34px] rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center"
            aria-hidden="true"
          >
            <Percent size={15} strokeWidth={2.2} />
          </span>
          <input
            id="cart-coupon-code"
            type="text"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Enter coupon code"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={40}
            className={`w-full h-[46px] rounded-full border border-[#D7E7D7] bg-white pl-12 pr-4 text-[14px] uppercase placeholder:normal-case placeholder:text-[#8A9199] text-emerald-950 ${focusRing}`}
          />
        </span>
        <button
          type="submit"
          disabled={!draftCode || !canApply}
          className={`h-[46px] px-5 rounded-full bg-[linear-gradient(90deg,#2E8B47,#1E7A3C)] text-white text-[14px] font-bold shadow-[0_6px_14px_-6px_rgba(46,139,71,0.6)] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none shrink-0 ${focusRing}`}
        >
          Apply
        </button>
      </form>
      {!canApply && !submittedCode && (
        <p className="text-xs text-ink-secondary mt-1.5">Codes can be applied once your cart is priced for a location we serve.</p>
      )}

      {/* Announced as it changes: checking, applied, or not applied (and why). */}
      <div role="status" aria-live="polite" className="empty:hidden">
        {status && (
          <div
            className={`mt-2.5 flex items-start gap-2 rounded-xl border px-3 py-2.5 text-sm ${toneClass[status.tone]}`}
            data-testid="coupon-status"
          >
            {React.createElement(ToneIcon[status.tone], {
              size: 16,
              className: `mt-0.5 shrink-0 ${status.tone === 'pending' ? 'animate-spin' : ''}`,
              'aria-hidden': true,
            })}
            <span className="min-w-0 flex-1 break-words">{status.text}</span>
            <button
              type="button"
              onClick={onRemove}
              className={`shrink-0 text-xs font-semibold underline ${focusRing}`}
              aria-label={`Remove coupon ${submittedCode}`}
            >
              Remove
            </button>
          </div>
        )}
      </div>

      {/* Suggestions from THIS quote's availableCoupons: codes the backend says
          this basket could use. Picking one only submits it; the next quote
          decides. */}
      {suggestions.length > 0 && (
        <div className="mt-3" data-testid="available-coupons">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-secondary mb-1.5">Available coupons</h3>
          <ul className="space-y-1.5">
            {suggestions.map((offer) => {
              const isCurrent = offer.code === submittedCode;
              const confirmed = isCurrent && result.state === 'applied';
              const label = offer.label || offer.name;
              // Some offers describe their benefit in the label already
              // ("Free delivery"); do not print the same words twice.
              const benefitText = offerBenefitText(offer);
              const benefit =
                benefitText && label && benefitText.trim().toLowerCase() === label.trim().toLowerCase() ? '' : benefitText;
              return (
                <li
                  key={offer.id || offer.code}
                  className="flex items-center gap-2.5 rounded-xl border border-dashed border-emerald-200 px-3 py-1.5"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-mono text-xs font-bold tracking-wide text-emerald-900">{offer.code}</span>
                    <span className="block text-xs text-ink-secondary break-words">
                      {label}
                      {benefit ? ` · ${benefit}` : ''}
                    </span>
                  </span>
                  {confirmed ? (
                    <span className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                      <CheckCircle2 size={13} aria-hidden="true" /> Applied
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => submit(offer.code)}
                      disabled={isCurrent || !canApply}
                      aria-label={`Apply coupon ${offer.code}`}
                      className={`shrink-0 text-xs font-semibold text-emerald-800 underline disabled:no-underline disabled:opacity-50 ${focusRing}`}
                    >
                      Apply
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CartCoupon;
